/**
 * Text Generation Service using Anthropic Claude
 * 
 * This service integrates with Anthropic Claude for high-quality text generation
 * with a fallback local implementation when the API is not available.
 */

import axios from 'axios';
import { StabilityAIStylePreset, generateImage } from './stabilityAIService-new';

// Types for Text Generation
export interface StabilityTextGenerationOptions {
  prompt: string;
  contentType: 'social' | 'email' | 'blog' | 'ad' | 'newsletter';
  tone?: 'formal' | 'casual' | 'professional' | 'friendly' | 'persuasive';
  industry?: string;
  targetAudience?: string;
  wordCount?: number;
  organizationName?: string;
  maxTokens?: number;
}

// Interface for parsed event data
interface ParsedEvent {
  title: string;
  description: string;
  date: string;
  location: string;
  imageUrl?: string;
}

// Interface for parsed trip data
interface ParsedTrip {
  title: string;
  description: string;
  imageUrl?: string;
  destination?: string;
  dateRange?: string;
  duration?: string;
  price?: string;
}

// Interface for parsed membership data
interface ParsedMembership {
  name: string;
  description: string;
  price: string;
  benefits: string[];
}

/**
 * Generates text content using Anthropic Claude with local fallback
 * 
 * @param options Configuration for text generation
 * @returns Generated text content
 */
export const generateTextContent = async (
   options: StabilityTextGenerationOptions
): Promise<string> => {
   try {
      // Extract event, trip, and membership data from the prompt
      const events = parseEvents(options.prompt);
      const trips = parseTrips(options.prompt);
      const memberships = parseMemberships(options.prompt);

      // Generate AI images for events and trips if needed
      await generateAIImagesForContent(events, trips);

      try {
         // Create a trimmed version of the options to reduce payload size
         const trimmedOptions = {
            prompt: options.prompt.substring(0, 2000), // Limit prompt size
            contentType: options.contentType,
            tone: options.tone,
            industry: options.industry,
            targetAudience: options.targetAudience,
            wordCount: options.wordCount,
            organizationName: options.organizationName,
            maxTokens: options.maxTokens || 1500,
         };

         // Call our server endpoint instead of directly calling Anthropic API
         // This avoids CORS issues as the server will make the request to Anthropic
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/anthropic/generate-text`,
            trimmedOptions,
            {
               headers: {
                  'Content-Type': 'application/json',
               },
            }
         );

         // Check if the API call was successful
         if (response.data && response.data.status === 'success') {
            return response.data.content;
         }

         throw new Error(response.data?.error || 'Unknown error from server');
      } catch (error) {
         console.error('Error calling server API for text generation:', error);
         console.warn('Falling back to local template generation');
      }

      // Fallback: Use local template-based generation

      return generateNewsletterTemplate(
         options.organizationName || 'Our Organization',
         events,
         trips,
         memberships,
         options.industry || 'Travel',
         options.targetAudience || 'Travelers'
      );
   } catch (error) {
      console.error('Error generating text:', error);

      if (error instanceof Error) {
         throw new Error(`Text generation failed: ${error.message}`);
      }

      throw new Error('Text generation failed with unknown error');
   }
};

/**
 * Creates a comprehensive prompt for Anthropic Claude
 */
function createAnthropicPrompt(
   options: StabilityTextGenerationOptions,
   events: ParsedEvent[],
   trips: ParsedTrip[],
   memberships: ParsedMembership[]
): string {
   // Determine a detailed instruction based on content type
   let contentTypeDetails;
   switch (options.contentType) {
      case 'social':
         contentTypeDetails = 'social media post that is concise, engaging, and shareable';
         break;
      case 'email':
         contentTypeDetails =
            'email with a clear subject line, personalized greeting, engaging body, and strong call-to-action';
         break;
      case 'blog':
         contentTypeDetails =
            'blog post with a compelling headline, introduction, well-structured body with subheadings, and conclusion';
         break;
      case 'ad':
         contentTypeDetails =
            'advertisement with attention-grabbing headline, clear value proposition, and persuasive call-to-action';
         break;
      case 'newsletter':
         contentTypeDetails =
            'newsletter with engaging subject line, personalized greeting, valuable content organized into sections, and actionable conclusion';
         break;
      default:
         contentTypeDetails = 'well-structured content';
   }

   let prompt = `You are an expert marketing copywriter creating a professional ${options.contentType} for ${options.organizationName || 'the organization'}.

The content should be a ${contentTypeDetails}.

Use a ${options.tone || 'professional'} tone and target audience is ${options.targetAudience || 'general'}.
${options.wordCount ? `The content should be approximately ${options.wordCount} words.` : ''}
${options.industry ? `The organization is in the ${options.industry} industry.` : ''}

The content should be based on this prompt: "${options.prompt}"

${events.length > 0 || trips.length > 0 || memberships.length > 0 ? 'Include these specific details:' : ''}
`;

   if (events.length > 0) {
      prompt += '\nEVENTS:\n';
      events.forEach((event) => {
         prompt += `- "${event.title}" on ${event.date}${event.location ? ` at ${event.location}` : ''}: ${event.description}\n`;
      });
   }

   if (trips.length > 0) {
      prompt += '\nTRIPS:\n';
      trips.forEach((trip) => {
         prompt += `- "${trip.title}" from ${trip.dateRange}${trip.destination ? `, destination: ${trip.destination}` : ''}: ${trip.description}\n`;
      });
   }

   if (memberships.length > 0) {
      prompt += '\nMEMBERSHIPS:\n';
      memberships.forEach((membership) => {
         prompt += `- "${membership.name}" at ${membership.price}: ${membership.description}\n`;
         if (membership.benefits.length > 0) {
            prompt += `  Benefits: ${membership.benefits.join(', ')}\n`;
         }
      });
   }

   prompt += `
Create well-formatted content that's engaging, clear, and persuasive.
Do not include any placeholders or template language in your response.
The final text should be ready to use without any further editing.
`;

   return prompt;
}

/**
 * Parse events from the prompt text
 */
function parseEvents(promptText: string): ParsedEvent[] {
   if (!promptText.includes('UPCOMING EVENTS')) {
      return [];
   }

   try {
      const eventSection = promptText.split('UPCOMING EVENTS:')[1]?.split('\n\n')[0] || '';
      if (!eventSection) return [];

      const eventLines = eventSection.split('\n').filter((line) => line.trim().startsWith('-'));

      return eventLines.map((line) => {
         const cleanLine = line.replace(/^-\s*/, '').trim();

         // Extract title and rest of the content
         const titleMatch = cleanLine.match(/^([^:]+):/);
         const title = titleMatch ? titleMatch[1].trim() : 'Event';

         // Extract description, date and location
         const restOfLine = cleanLine.replace(/^[^:]+:\s*/, '');

         const dateMatch =
            restOfLine.match(/on\s+([A-Za-z]+\s+\d+,\s+\d{4})/i) ||
            restOfLine.match(/on\s+([A-Za-z]+\s+\d+)/i);
         const date = dateMatch ? dateMatch[1] : 'TBD';

         const locationMatch = restOfLine.match(/at\s+([^,|]+)(?:,\s*([^,|]+))?/i);
         const location = locationMatch ? locationMatch[1] : '';

         // Extract image URL if present
         const imageUrlMatch = restOfLine.match(/\|IMAGE:(.*?)\|/);
         const imageUrl = imageUrlMatch
            ? imageUrlMatch[1]
            : 'https://via.placeholder.com/300x200?text=Event+Image';

         // Extract description - anything that's not date, location or image
         let description = restOfLine;
         if (dateMatch) description = description.replace(dateMatch[0], '');
         if (locationMatch) description = description.replace(locationMatch[0], '');
         if (imageUrlMatch) description = description.replace(imageUrlMatch[0], '');
         description = description.trim();

         return {
            title,
            description,
            date,
            location,
            imageUrl,
         };
      });
   } catch (error) {
      console.error('Error parsing events:', error);
      return [];
   }
}

/**
 * Parse trips from the prompt text
 */
function parseTrips(promptText: string): ParsedTrip[] {
   if (!promptText.includes('UPCOMING TRIPS')) {
      return [];
   }

   try {
      const tripSection = promptText.split('UPCOMING TRIPS:')[1]?.split('\n\n')[0] || '';
      if (!tripSection) return [];

      const tripLines = tripSection.split('\n').filter((line) => line.trim().startsWith('-'));

      return tripLines.map((line) => {
         const cleanLine = line.replace(/^-\s*/, '').trim();

         // Extract title and rest of the content
         const titleMatch = cleanLine.match(/^([^:]+):/);
         const title = titleMatch ? titleMatch[1].trim() : 'Trip';

         // Extract description, date range and destination
         const restOfLine = cleanLine.replace(/^[^:]+:\s*/, '');

         const dateRangeMatch = restOfLine.match(
            /from\s+([A-Za-z]+\s+\d+,\s+\d{4}|[A-Za-z]+\s+\d+)\s+to\s+([A-Za-z]+\s+\d+,\s+\d{4}|[A-Za-z]+\s+\d+)/i
         );
         const dateRange = dateRangeMatch ? `${dateRangeMatch[1]} to ${dateRangeMatch[2]}` : 'TBD';

         const destinationMatch = restOfLine.match(/destination:\s+([^,|]+)(?:,\s*([^,|]+))?/i);
         const destination = destinationMatch ? destinationMatch[1] : '';

         // Extract image URL if present
         const imageUrlMatch = restOfLine.match(/\|IMAGE:(.*?)\|/);
         const imageUrl = imageUrlMatch
            ? imageUrlMatch[1]
            : title.toLowerCase().includes('miami')
              ? 'https://images.unsplash.com/photo-1535498730771-e735b998cd64?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1587&q=80'
              : title.toLowerCase().includes('canada')
                ? 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1611&q=80'
                : 'https://via.placeholder.com/400x300?text=Trip+Image';

         // Extract description - anything that's not date range, destination or image
         let description = restOfLine;
         if (dateRangeMatch) description = description.replace(dateRangeMatch[0], '');
         if (destinationMatch) description = description.replace(destinationMatch[0], '');
         if (imageUrlMatch) description = description.replace(imageUrlMatch[0], '');
         description = description.trim();

         return {
            title,
            description,
            dateRange,
            destination,
            imageUrl,
         };
      });
   } catch (error) {
      console.error('Error parsing trips:', error);
      return [];
   }
}

/**
 * Parse memberships from the prompt text
 */
function parseMemberships(promptText: string): ParsedMembership[] {
   if (!promptText.includes('MEMBERSHIP OPTIONS')) {
      return [];
   }

   try {
      const membershipSection = promptText.split('MEMBERSHIP OPTIONS:')[1]?.split('\n\n')[0] || '';
      if (!membershipSection) return [];

      const membershipLines = membershipSection
         .split('\n')
         .filter((line) => line.trim().startsWith('-'));

      return membershipLines.map((line) => {
         const cleanLine = line.replace(/^-\s*/, '').trim();

         // Extract name and rest of the content
         const nameMatch = cleanLine.match(/^([^:]+):/);
         const name = nameMatch ? nameMatch[1].trim() : 'Membership';

         // Extract description, price and benefits
         const restOfLine = cleanLine.replace(/^[^:]+:\s*/, '');

         const priceMatch = restOfLine.match(/\$(\d+(?:\.\d+)?)\s+([A-Za-z]+)/);
         const price = priceMatch ? `$${priceMatch[1]} ${priceMatch[2]}` : 'Price varies';

         const benefitsMatch = restOfLine.match(/Benefits:\s+(.+)$/);
         const benefits = benefitsMatch ? benefitsMatch[1].split(', ').map((b) => b.trim()) : [];

         // Extract description - anything that's not price or benefits
         let description = restOfLine;
         if (priceMatch) description = description.replace(priceMatch[0], '');
         if (benefitsMatch) description = description.replace(benefitsMatch[0], '');
         description = description.trim();

         return {
            name,
            description,
            price,
            benefits,
         };
      });
   } catch (error) {
      console.error('Error parsing memberships:', error);
      return [];
   }
}

/**
 * Generate a comprehensive newsletter template with images
 */
function generateNewsletterTemplate(
   organizationName: string,
   events: ParsedEvent[],
   trips: ParsedTrip[],
   memberships: ParsedMembership[],
   industry: string,
   targetAudience: string
): string {
   const currentDate = new Date();
   const formattedDate = currentDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
   });

   // Generate cover image - preferably based on trips if available,
   // otherwise based on events or a generic travel image
   const coverImageInfo = generateNewsletterCoverImage(organizationName, trips, events);

   let newsletter = `# ${organizationName} Newsletter: ${formattedDate}\n\n`;

   // Add cover image at the top of the newsletter
   newsletter += `![${coverImageInfo.alt}](${coverImageInfo.url})\n\n`;

   newsletter += `Dear Valued ${organizationName} Members,\n\n`;

   newsletter += `We're excited to share with you the latest updates from ${organizationName}. As a leading provider in the travel and tourism industry, we're constantly working to bring you exceptional experiences and opportunities.\n\n`;

   // Add Events Section if there are events
   if (events.length > 0) {
      newsletter += `## UPCOMING EVENTS\n\n`;

      // Loop through each event and add it to the newsletter
      events.forEach((event) => {
         newsletter += `### ${event.title}\n\n`;
         if (event.imageUrl) {
            newsletter += `![${event.title}](${event.imageUrl})\n\n`;
         }
         if (event.date) {
            newsletter += `**Date:** ${event.date}\n\n`;
         }
         if (event.location) {
            newsletter += `**Location:** ${event.location}\n\n`;
         }
         if (event.description) {
            newsletter += `${event.description}\n\n`;
         }
         newsletter += `---\n\n`;
      });
   }

   // Add Trips Section if there are trips
   if (trips.length > 0) {
      newsletter += `## FEATURED TRIPS\n\n`;

      // Loop through each trip and add it to the newsletter
      trips.forEach((trip) => {
         newsletter += `### ${trip.title}\n\n`;
         if (trip.imageUrl) {
            newsletter += `![${trip.title}](${trip.imageUrl})\n\n`;
         }
         if (trip.duration) {
            newsletter += `**Duration:** ${trip.duration}\n\n`;
         }
         if (trip.price) {
            newsletter += `**Price:** ${trip.price}\n\n`;
         }
         if (trip.description) {
            newsletter += `${trip.description}\n\n`;
         }
         newsletter += `---\n\n`;
      });
   }

   // Add Memberships Section if there are memberships
   if (memberships.length > 0) {
      newsletter += `## MEMBERSHIP BENEFITS\n\n`;

      // Loop through each membership and add it to the newsletter
      memberships.forEach((membership) => {
         newsletter += `### ${membership.name}\n\n`;
         if (membership.price) {
            newsletter += `**Price:** ${membership.price}\n\n`;
         }
         if (membership.benefits) {
            newsletter += `**Benefits:**\n\n`;
            membership.benefits.forEach((benefit) => {
               newsletter += `- ${benefit}\n`;
            });
            newsletter += `\n`;
         }
         if (membership.description) {
            newsletter += `${membership.description}\n\n`;
         }
         newsletter += `---\n\n`;
      });
   }

   // Add footer
   newsletter += `Thank you for being a valued member of the ${organizationName} community. We look forward to seeing you at our upcoming events and trips!\n\n`;
   newsletter += `Best regards,\n\n`;
   newsletter += `The ${organizationName} Team\n\n`;

   return newsletter;
}

/**
 * Generate a cover image for the newsletter based on selected content
 */
function generateNewsletterCoverImage(
   organizationName: string,
   trips: ParsedTrip[],
   events: ParsedEvent[]
): { url: string; alt: string } {
   // Try to use the first trip's image as the cover if available
   if (trips.length > 0 && trips[0].imageUrl) {
      return {
         url: trips[0].imageUrl,
         alt: `${organizationName} Newsletter Cover - ${trips[0].title}`,
      };
   }

   // Otherwise try to use the first event's image
   if (events.length > 0 && events[0].imageUrl) {
      return {
         url: events[0].imageUrl,
         alt: `${organizationName} Newsletter Cover - ${events[0].title}`,
      };
   }

   // If no trips or events with images, use a generic travel newsletter cover
   return {
      url: 'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
      alt: `${organizationName} Newsletter Cover`,
   };
}

/**
 * Generate AI images for events and trips
 */
async function generateAIImagesForContent(events: ParsedEvent[], trips: ParsedTrip[]) {
   // Function to generate an image for an entity (event or trip)
   const generateEntityImage = async (
      title: string,
      description: string,
      type: 'event' | 'trip'
   ) => {
      // Skip generation if we don't have Stability AI API key
      if (!process.env.REACT_APP_STABILITY_AI_API_KEY) {
         return getDefaultImageForType(title, type);
      }

      try {
         // Add some delay to avoid rate limiting
         await new Promise((resolve) => setTimeout(resolve, 500));

         // Create a descriptive prompt for the AI
         let prompt = '';
         if (type === 'event') {
            prompt = `Professional event photo for "${title}". ${description ? description : 'A well-organized event with people attending.'} High quality, detailed, photorealistic.`;
         } else {
            prompt = `Beautiful travel destination photo for "${title}". ${description ? description : 'A picturesque travel destination with stunning views.'} Scenic, atmospheric, photorealistic.`;
         }

         // Select an appropriate style preset
         const stylePreset: StabilityAIStylePreset = type === 'event' ? 'photographic' : 'enhance';

         // Generate the image
         const imageResponse = await generateImage({
            prompt,
            stylePreset,
            negativePrompt:
               'distorted, blurry, low quality, cartoon, drawing, painting, sketch, watermark, text, logo',
         });

         // If successful, return the image URL
         if (imageResponse && imageResponse.images && imageResponse.images.length > 0) {
            // In a real implementation, we would use the base64 image data to create a URL
            // For now, we'll use placeholder images to simulate this
            return `data:image/png;base64,${imageResponse.images[0].base64}`;
         }
      } catch (error) {
         console.error(`Error generating image for ${type} "${title}":`, error);
      }

      // If generation fails, return a default image
      return getDefaultImageForType(title, type);
   };

   // Process events
   for (const event of events) {
      if (!event.imageUrl || event.imageUrl.includes('placeholder.com')) {
         event.imageUrl = await generateEntityImage(event.title, event.description, 'event');
      }
   }

   // Process trips
   for (const trip of trips) {
      if (!trip.imageUrl || trip.imageUrl.includes('placeholder.com')) {
         trip.imageUrl = await generateEntityImage(trip.title, trip.description, 'trip');
      }
   }
}

/**
 * Get a default image URL based on entity type and title
 */
function getDefaultImageForType(title: string, type: 'event' | 'trip'): string {
  const titleLower = title.toLowerCase();
  
  // Event-specific images
  if (type === 'event') {
    if (titleLower.includes('party') || titleLower.includes('celebration')) {
      return 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    if (titleLower.includes('conference') || titleLower.includes('meeting')) {
      return 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    if (titleLower.includes('wedding')) {
      return 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    if (titleLower.includes('workshop')) {
      return 'https://images.unsplash.com/photo-1544531585-9847b68c8c86?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    if (titleLower.includes('lagos') || titleLower.includes('nigeria')) {
      return 'https://images.unsplash.com/photo-1577253313708-cab167d2c474?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    
    // Default event image
    return 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1169&q=80';
  }
  
  // Trip-specific images
  if (type === 'trip') {
    if (titleLower.includes('miami')) {
      return 'https://images.unsplash.com/photo-1535498730771-e735b998cd64?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1587&q=80';
    }
    if (titleLower.includes('canada')) {
      return 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1611&q=80';
    }
    if (titleLower.includes('spain')) {
      return 'https://images.unsplash.com/photo-1543783207-ec64e4d95325?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
    }
    if (titleLower.includes('weekend')) {
      return 'https://images.unsplash.com/photo-1533105079780-92b9be482077?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1587&q=80';
    }
    
    // Default trip image
    return 'https://images.unsplash.com/photo-1488085061387-422e29b40080?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1331&q=80';
  }
  
  // Generic default
  return 'https://images.unsplash.com/photo-1493612276216-ee3925520721?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
}
